import { useState } from "react";
import SectionHeading from "@/components/base/SectionHeading";
import Reveal from "@/components/base/Reveal";
import { talkThreads, talkComments, type TalkComment } from "@/mocks/home";

export default function CommunityTalk() {
  const [comments, setComments] = useState<TalkComment[]>(talkComments);
  const [draft, setDraft] = useState("");
  const [notice, setNotice] = useState("");

  const submit = () => {
    const text = draft.trim();
    if (!text) {
      setNotice("先写点什么，再按下发送吧");
      return;
    }
    if (text.length > 200) {
      setNotice("留言请控制在 200 字以内");
      return;
    }
    setComments((prev) => [
      { user: "我", text, likes: 0, time: "刚刚", badge: "新留言" },
      ...prev,
    ]);
    setDraft("");
    setNotice("已发布到当前讨论（演示展示，不会真实提交）");
  };

  return (
    <section id="talk" className="w-full border-b border-background-200 bg-background-50">
      <div className="mx-auto w-full max-w-[1280px] px-4 py-16 md:px-8 md:py-20">
        <Reveal>
          <SectionHeading
            eyebrow="话题讨论"
            tone="secondary"
            title="一区的氛围，是靠讲证据聊出来的"
            description="这里不追着情绪跑。补充来源、纠正时间线、提醒未证实的部分，都是社区里最受欢迎的发言方式。"
          />
        </Reveal>

        <div className="mt-10 grid grid-cols-1 gap-6 lg:grid-cols-5">
          <div className="lg:col-span-3">
            <Reveal delay={60}>
              <div className="rounded-lg border border-background-200 bg-background-100 p-5">
                <h3 className="flex items-center gap-2 font-heading text-lg font-bold text-foreground-950">
                  <i className="ri-chat-history-line text-primary-600" />
                  正在进行的讨论
                </h3>
                <ul className="mt-4 flex flex-col gap-3">
                  {talkThreads.map((thread) => (
                    <li
                      key={thread.title}
                      className="rounded-lg border border-background-200 bg-background-50 p-4 transition-colors hover:border-primary-300"
                    >
                      <div className="flex flex-wrap items-center gap-2">
                        <span className="rounded-full bg-secondary-100 px-2.5 py-0.5 text-xs text-secondary-900">
                          {thread.tag}
                        </span>
                        <span className="text-xs text-foreground-500">{thread.active}</span>
                      </div>
                      <a
                        href="#talk"
                        className="mt-2 block text-base font-bold leading-snug text-foreground-950 transition-colors hover:text-primary-700"
                      >
                        {thread.title}
                      </a>
                      <p className="clamp-2 mt-2 text-sm leading-relaxed text-foreground-600">
                        {thread.excerpt}
                      </p>
                      <div className="mt-3 flex items-center gap-1 text-xs text-foreground-500">
                        <i className="ri-chat-3-line" />
                        {thread.replies} 条回复
                      </div>
                    </li>
                  ))}
                </ul>
              </div>
            </Reveal>
          </div>

          <div className="lg:col-span-2">
            <Reveal delay={120}>
              <div className="rounded-lg border border-background-200 bg-background-50 p-5">
                <h3 className="flex items-center gap-2 font-heading text-lg font-bold text-foreground-950">
                  <i className="ri-thumb-up-line text-accent-600" />
                  高赞留言
                </h3>
                <ul className="mt-4 flex flex-col divide-y divide-background-200">
                  {comments.map((comment, index) => (
                    <li key={`${comment.user}-${index}`} className="py-4 first:pt-0">
                      <div className="flex items-center justify-between">
                        <div className="flex items-center gap-2">
                          <span className="flex h-7 w-7 items-center justify-center rounded-full bg-primary-100 text-xs font-medium text-primary-800">
                            {comment.user.slice(0, 1)}
                          </span>
                          <span className="text-sm font-medium text-foreground-800">
                            {comment.user}
                          </span>
                          {comment.badge && (
                            <span className="rounded-full bg-primary-100 px-2 py-0.5 text-[10px] text-primary-800">
                              {comment.badge}
                            </span>
                          )}
                        </div>
                        <span className="text-xs text-foreground-500">{comment.time}</span>
                      </div>
                      <p className="mt-2 text-sm leading-relaxed text-foreground-700">
                        {comment.text}
                      </p>
                      <div className="mt-2 inline-flex items-center gap-1 text-xs text-foreground-500">
                        <i className="ri-thumb-up-line" />
                        {comment.likes}
                      </div>
                    </li>
                  ))}
                </ul>

                <div className="mt-4 rounded-lg border border-background-200 bg-background-100 p-3">
                  <label htmlFor="talk-input" className="text-xs text-foreground-500">
                    说说你的看法（演示功能，不会真实提交）
                  </label>
                  <textarea
                    id="talk-input"
                    name="talk_message"
                    value={draft}
                    maxLength={200}
                    onChange={(e) => setDraft(e.target.value)}
                    rows={3}
                    placeholder="补充来源、纠正时间线，或者说说你的判断…"
                    className="mt-2 w-full resize-none rounded-md border border-background-300 bg-background-50 px-3 py-2 text-sm text-foreground-900 outline-none transition-colors placeholder:text-foreground-400 focus:border-primary-400"
                  />
                  <div className="mt-2 flex items-center justify-between">
                    <span className="text-xs text-foreground-500">{draft.length}/200</span>
                    <button
                      type="button"
                      onClick={submit}
                      className="inline-flex cursor-pointer items-center gap-1.5 whitespace-nowrap rounded-md bg-primary-500 px-4 py-2 text-sm font-medium text-background-50 transition-colors hover:bg-primary-600"
                    >
                      <i className="ri-send-plane-2-line" />
                      发送留言
                    </button>
                  </div>
                  {notice && (
                    <p className="mt-2 inline-flex items-center gap-1 text-xs text-accent-700">
                      <i className="ri-checkbox-circle-line" />
                      {notice}
                    </p>
                  )}
                </div>
              </div>
            </Reveal>
          </div>
        </div>
      </div>
    </section>
  );
}